import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

interface TrendData {
  date: string;
  sent: number;
  completed: number;
  overdue: number;
  in_progress: number;
  draft?: number;
}

interface SubmissionTrendsChartProps {
  data: TrendData[];
  period: string;
}

const SubmissionTrendsChart: React.FC<SubmissionTrendsChartProps> = ({ data, period }) => {
  const maxValue = Math.max(...data.map(d => (d.sent || 0) + (d.completed || 0) + (d.overdue || 0) + (d.in_progress || 0) + (d.draft || 0)), 1);
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getBarHeight = (value: number) => {
    return maxValue > 0 ? Math.max((value / maxValue) * 100, 2) : 2;
  };

  const getTotalForDate = (item: TrendData) => {
    return (item.sent || 0) + (item.completed || 0) + (item.overdue || 0) + (item.in_progress || 0) + (item.draft || 0);
  };

  // **Berechne Gesamtstatistiken**
  const totalStats = data.reduce((acc, item) => ({
    sent: acc.sent + (item.sent || 0),
    completed: acc.completed + (item.completed || 0),
    in_progress: acc.in_progress + (item.in_progress || 0),
    overdue: acc.overdue + (item.overdue || 0),
    draft: acc.draft + (item.draft || 0)
  }), { sent: 0, completed: 0, in_progress: 0, overdue: 0, draft: 0 });

  const grandTotal = totalStats.sent + totalStats.completed + totalStats.in_progress + totalStats.overdue + totalStats.draft;

  const statusConfig = [
    { key: 'draft', label: 'Entwürfe', color: 'bg-purple-500', lightColor: 'bg-purple-100', textColor: 'text-purple-700' },
    { key: 'sent', label: 'Gesendet', color: 'bg-blue-500', lightColor: 'bg-blue-100', textColor: 'text-blue-700' },
    { key: 'in_progress', label: 'In Bearbeitung', color: 'bg-yellow-500', lightColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
    { key: 'completed', label: 'Abgeschlossen', color: 'bg-green-500', lightColor: 'bg-green-100', textColor: 'text-green-700' },
    { key: 'overdue', label: 'Überfällig', color: 'bg-red-500', lightColor: 'bg-red-100', textColor: 'text-red-700' }
  ];

  // **HELPER: Get value for status safely**
  const getStatusValue = (item: TrendData, statusKey: string): number => {
    switch (statusKey) {
      case 'draft': return item.draft || 0;
      case 'sent': return item.sent || 0;
      case 'in_progress': return item.in_progress || 0;
      case 'completed': return item.completed || 0;
      case 'overdue': return item.overdue || 0;
      default: return 0;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
    >
      {/* **HEADER** */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-blue-500 p-3 rounded-lg mr-4">
              <FontAwesomeIcon icon={faChartLine} className="text-white text-lg" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Submission Trends</h3>
              <p className="text-sm text-gray-600 mt-1">
                {period === '7d' ? 'Letzte 7 Tage' : 
                 period === '30d' ? 'Letzte 30 Tage' : 
                 period === '90d' ? 'Letzte 90 Tage' : 'Letztes Jahr'}
              </p>
            </div>
          </div>
          
          {/* **TOTAL SUMMARY** */}
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">{grandTotal}</div>
            <div className="text-sm text-gray-600">Gesamt Submissions</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* **STATUS LEGEND** */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {statusConfig.map(status => (
            <div key={status.key} className="flex items-center">
              <div className={`w-4 h-4 rounded ${status.color} mr-2`}></div>
              <span className="text-sm font-medium text-gray-700">{status.label}</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-bold ${status.lightColor} ${status.textColor}`}>
                {totalStats[status.key as keyof typeof totalStats]}
              </span>
            </div>
          ))}
        </div>

        {/* **CHART** */}
        {grandTotal > 0 ? (
          <div className="h-80 flex items-end justify-between gap-1 bg-gray-50 rounded-lg p-4">
            {data.map((item, index) => {
              const total = getTotalForDate(item);
              
              return (
                <div key={item.date} className="flex-1 flex flex-col items-center group">
                  <div className="w-full flex flex-col justify-end h-64 relative">
                    {total > 0 ? (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${getBarHeight(total)}%` }}
                        transition={{ duration: 0.6, delay: index * 0.03 }}
                        className="w-full flex flex-col justify-end rounded-t-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                        style={{ minHeight: '8px' }}
                      >
                        {/* **STACKED BAR SECTIONS** */}
                        {statusConfig.map(status => {
                          const value = getStatusValue(item, status.key);
                          if (value === 0) return null;
                          
                          return (
                            <div
                              key={status.key}
                              className={`w-full ${status.color}`}
                              style={{ height: `${(value / total) * 100}%` }}
                            />
                          );
                        })}

                        {/* **TOOLTIP** */}
                        <div className="opacity-0 group-hover:opacity-100 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 bg-gray-900 text-white text-xs rounded-lg py-3 px-4 pointer-events-none transition-opacity z-20 shadow-xl">
                          <div className="text-center space-y-1">
                            <div className="font-bold text-blue-200">{formatDate(item.date)}</div>
                            <div className="border-t border-gray-700 pt-1">
                              <div className="font-semibold">Gesamt: {total}</div>
                              {statusConfig.map(status => {
                                const value = getStatusValue(item, status.key);
                                if (value === 0) return null;
                                return (
                                  <div key={status.key} className="flex justify-between">
                                    <span>{status.label}:</span>
                                    <span className="font-medium">{value}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </motion.div>
                    ) : (
                      <div className="w-full h-2 bg-gray-200 rounded"></div>
                    )}
                  </div>

                  <div className="text-xs text-gray-600 mt-3 font-medium">
                    {formatDate(item.date)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <FontAwesomeIcon icon={faInfoCircle} className="text-gray-400 text-5xl mb-4" />
              <p className="text-gray-600 font-medium text-lg">Keine Submissions im gewählten Zeitraum</p>
              <p className="text-gray-500 text-sm mt-2">Erstellen Sie Fragebögen oder senden Sie diese an Lieferanten</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SubmissionTrendsChart;